import React from "react";
import { Box, Typography, Avatar, Button, useMediaQuery } from "@mui/material";
import { theme } from "../../theme";
import { ROUTES } from "../../constants/routes";
import { User } from "../../types/frontend";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { searchResultStyles } from "../../pages/styles";

/**
 * Props for the SearchResult component.
 * @property hit - The Algolia search result (user) to display.
 */
export interface SearchResultProps {
  hit: UserHit;
}

/**
 * Represents a hit/result from Algolia search.
 * Extends the User type with Algolia-specific fields.
 */
export interface UserHit extends User {
  objectID: string;
  _highlightResult?: {
    [key: string]: {
      value: string;
      matchLevel: string;
      matchedWords: string[];
    };
  };
}

/**
 * SearchResult Component
 *
 * Displays a single user search result from Algolia.
 * Shows avatar, name, institute, and a button to view the profile.
 * The entire row is clickable and opens the user's profile in a new tab.
 *
 * @param {SearchResultProps} props - The props for the component.
 * @returns {JSX.Element} The rendered search result row.
 */
const SearchResult: React.FC<SearchResultProps> = ({ hit }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  /**
   * Handles click event for the result row or button.
   * Opens the user's profile in a new browser tab.
   */
  const handleClick = () => {
    window.open(`${ROUTES.USER_DIRECTORY}/${hit._id}`, "_blank");
  };

  return (
    // Outer container for the search result row
    <Box sx={searchResultStyles.container} onClick={handleClick}>
      {/* Row content: avatar, user info, and view profile button */}
      <Box sx={searchResultStyles.contentRow}>
        {/* Avatar and user info */}
        <Box sx={searchResultStyles.userInfo}>
          {/* User avatar */}
          <Avatar
            src={getStaticAssetPath(hit.image || "")}
            alt={hit.name}
            sx={searchResultStyles.avatar}
          >
            {/* Fallback: first letter of name */}
            {hit.name.charAt(0).toUpperCase()}
          </Avatar>

          {/* User name and institute */}
          <Box sx={searchResultStyles.userDetails}>
            {/* User name */}
            <Typography
              variant="h6"
              component="h3"
              sx={searchResultStyles.userName}
            >
              {hit.name}
            </Typography>

            {/* User institute, if available */}
            {hit.institute && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={searchResultStyles.userInstitute}
              >
                {hit.institute}
              </Typography>
            )}
          </Box>
        </Box>

        {/* View Profile button */}
        {!isMobile && (
          <Button
            variant="contained"
            sx={searchResultStyles.viewButton(theme)}
            onClick={handleClick}
            disableRipple
          >
            View Profile
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SearchResult;
